import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {createGeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {Operators} from "#template/geometry-processing/geometry-graph"
import {
    applyGeometryOperator,
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    geometryAttributeRefDataSetToGeometryAttributeSetData,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import type {AttributeRefSet} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export const PRODUCT_OPERATOR_OUTPUT_A = "ProductOperator.attributeSetA"
export const PRODUCT_OPERATOR_OUTPUT_B = "ProductOperator.attributeSetB"

// Cross-dim input pair: each side carries an attribute + attribute-set; the variant pins per-side
// types (e.g. point/point, point/curve, curve/mesh).
export type ProductOperatorPairParameters<TPrimaryA, TAttributeSetA, TPrimaryB, TAttributeSetB> = {
    primaryAttributeA: TPrimaryA | null
    attributeSetA: TAttributeSetA | null
    primaryAttributeB: TPrimaryB | null
    attributeSetB: TAttributeSetB | null
}

export const productOperatorPairParametersSchema = <TPrimaryA, TAttributeSetA, TPrimaryB, TAttributeSetB>(
    primaryASchema: z.ZodMiniType<TPrimaryA, TPrimaryA>,
    attributeSetASchema: z.ZodMiniType<TAttributeSetA, TAttributeSetA>,
    primaryBSchema: z.ZodMiniType<TPrimaryB, TPrimaryB>,
    attributeSetBSchema: z.ZodMiniType<TAttributeSetB, TAttributeSetB>,
) =>
    z.object({
        primaryAttributeA: z.nullable(primaryASchema),
        attributeSetA: z.nullable(attributeSetASchema),
        primaryAttributeB: z.nullable(primaryBSchema),
        attributeSetB: z.nullable(attributeSetBSchema),
    })

function applyProductOperator<TOut extends IndexingDim, AT extends IndexingDim, BT extends IndexingDim>(
    primaryAttributeA: GeometryAttributeData<AT>,
    attributeSetA: GeometryAttributeSetData<AT>,
    primaryAttributeB: GeometryAttributeData<BT>,
    attributeSetB: GeometryAttributeSetData<BT>,
    outputIndexingDim: TOut,
): GeometryAttributeSetData<TOut> {
    const primaryAttrRefDataA = primaryAttributeA.ref
    const primaryAttrRefDataB = primaryAttributeB.ref

    const attrRefDataSetA = geometryAttributeSetDataToGeometryAttributeRefDataSet(attributeSetA)
    const attrRefDataSetB = geometryAttributeSetDataToGeometryAttributeRefDataSet(attributeSetB)

    const productAttrRefDataSet = applyGeometryOperator(
        (primaryAttrRefA: AttributeRef, attrRefSetA: AttributeRefSet, primaryAttrRefB: AttributeRef, attrRefSetB: AttributeRefSet) => {
            const [productAttrRefSetA, productAttrRefSetB] = Operators.product(primaryAttrRefA, attrRefSetA, primaryAttrRefB, attrRefSetB)
            return {
                productAttrRefSetA,
                productAttrRefSetB,
            }
        },
        primaryAttrRefDataA,
        attrRefDataSetA,
        primaryAttrRefDataB,
        attrRefDataSetB,
    )

    const productAttributeSetDataA = geometryAttributeRefDataSetToGeometryAttributeSetData(productAttrRefDataSet.productAttrRefSetA, outputIndexingDim)
    const productAttributeSetDataB = geometryAttributeRefDataSetToGeometryAttributeSetData(productAttrRefDataSet.productAttrRefSetB, outputIndexingDim)

    return createGeometryAttributeSetData(
        {[`${PRODUCT_OPERATOR_OUTPUT_A}`]: productAttributeSetDataA, [`${PRODUCT_OPERATOR_OUTPUT_B}`]: productAttributeSetDataB},
        outputIndexingDim,
    )
}

// `T` is the output dim (the variant's own dim). Slot generics let each variant pin per-side attribute / attribute-set types.
export abstract class GeometryProductOperatorPairNode<
    out T extends IndexingDim,
    out TPrimaryA extends EvaluableTemplateNode<GeometryAttributeData<IndexingDim> | null>,
    out TAttributeSetA extends EvaluableTemplateNode<GeometryAttributeSetData<IndexingDim> | null>,
    out TPrimaryB extends EvaluableTemplateNode<GeometryAttributeData<IndexingDim> | null>,
    out TAttributeSetB extends EvaluableTemplateNode<GeometryAttributeSetData<IndexingDim> | null>,
> extends GeometryAttributeSetNode<T, ProductOperatorPairParameters<TPrimaryA, TAttributeSetA, TPrimaryB, TAttributeSetB>> {
    protected override hasValidParameters(
        parameters: ProductOperatorPairParameters<TPrimaryA, TAttributeSetA, TPrimaryB, TAttributeSetB>,
    ): parameters is ProductOperatorPairParameters<TPrimaryA, TAttributeSetA, TPrimaryB, TAttributeSetB> &
        NonNullableParameters<ProductOperatorPairParameters<TPrimaryA, TAttributeSetA, TPrimaryB, TAttributeSetB>> {
        return (
            parameters.primaryAttributeA !== null &&
            parameters.attributeSetA !== null &&
            parameters.primaryAttributeB !== null &&
            parameters.attributeSetB !== null
        )
    }

    protected abstract get outputIndexingDim(): T

    protected override setupGeometryData(
        parameters: NonNullableParameters<ProductOperatorPairParameters<TPrimaryA, TAttributeSetA, TPrimaryB, TAttributeSetB>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {primaryAttributeA, attributeSetA, primaryAttributeB, attributeSetB} = parameters

        const [evaluatedPrimaryAttributeAValid, evaluatedPrimaryAttributeAInvalid] = scope.branch(evaluator.evaluate(primaryAttributeA))
        const [evaluatedAttributeSetAValid, evaluatedAttributeSetAInvalid] = scope.branch(evaluator.evaluate(attributeSetA))
        const [evaluatedPrimaryAttributeBValid, evaluatedPrimaryAttributeBInvalid] = scope.branch(evaluator.evaluate(primaryAttributeB))
        const [evaluatedAttributeSetBValid, evaluatedAttributeSetBInvalid] = scope.branch(evaluator.evaluate(attributeSetB))

        const outputIndexingDim = this.outputIndexingDim

        const geometryAttributeSetData = scope.pureLambda(
            scope.tuple(
                evaluatedPrimaryAttributeAValid,
                evaluatedAttributeSetAValid,
                evaluatedPrimaryAttributeBValid,
                evaluatedAttributeSetBValid,
                outputIndexingDim,
            ),
            ([primaryAttributeAData, attributeSetAData, primaryAttributeBData, attributeSetBData, outputIndexingDim]) =>
                applyProductOperator(primaryAttributeAData, attributeSetAData, primaryAttributeBData, attributeSetBData, outputIndexingDim),
            "applyProductOperator",
        )

        return {
            evaluatedGeometryData: geometryAttributeSetData,
            invalidator: scope.phi(
                evaluatedPrimaryAttributeAInvalid,
                evaluatedAttributeSetAInvalid,
                evaluatedPrimaryAttributeBInvalid,
                evaluatedAttributeSetBInvalid,
            ),
        }
    }
}
